import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { paymentWebhookService, PaymentWebhookData, PaymentWebhookResponse } from '../../services/paymentWebhookService';

export interface PaymentWebhookState {
    isLoading: boolean;
    error: string | null;
    success: boolean;
    webhookResponse: PaymentWebhookResponse | null;
    lastProcessedBookingRef: string | null;
}

const initialState: PaymentWebhookState = {
    isLoading: false,
    error: null,
    success: false,
    webhookResponse: null,
    lastProcessedBookingRef: null,
};

// Async thunk for sending payment webhook
export const sendPaymentWebhook = createAsyncThunk(
    'paymentWebhook/sendWebhook',
    async (webhookData: PaymentWebhookData, { rejectWithValue }) => {
        try {
            const response = await paymentWebhookService.sendPaymentWebhook(webhookData);
            return response;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to send payment webhook'
            );
        }
    }
);

// Async thunk for verifying payment status
export const verifyPaymentStatus = createAsyncThunk(
    'paymentWebhook/verifyStatus',
    async (bookingRef: string, { rejectWithValue }) => {
        try {
            const response = await paymentWebhookService.verifyPaymentStatus(bookingRef);
            return response;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to verify payment status'
            );
        }
    }
);

const paymentWebhookSlice = createSlice({
    name: 'paymentWebhook',
    initialState,
    reducers: {
        clearWebhookState: (state) => {
            state.isLoading = false;
            state.error = null;
            state.success = false;
            state.webhookResponse = null;
        },
        clearWebhookError: (state) => {
            state.error = null;
        },
        clearWebhookSuccess: (state) => {
            state.success = false;
            state.webhookResponse = null;
        },
        setLastProcessedBookingRef: (state, action: PayloadAction<string>) => {
            state.lastProcessedBookingRef = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Send payment webhook
        builder
            .addCase(sendPaymentWebhook.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(sendPaymentWebhook.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
                state.webhookResponse = action.payload;
                state.error = null;
            })
            .addCase(sendPaymentWebhook.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                state.success = false;
            });

        // Verify payment status
        builder
            .addCase(verifyPaymentStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(verifyPaymentStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                state.webhookResponse = action.payload;
                state.error = null;
            })
            .addCase(verifyPaymentStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    clearWebhookState,
    clearWebhookError,
    clearWebhookSuccess,
    setLastProcessedBookingRef,
} = paymentWebhookSlice.actions;

export default paymentWebhookSlice.reducer;